import React, { useMemo, useRef } from 'react';
import { Group, Vector2Tuple } from 'three';

import { GroupProps } from '@react-three/fiber';
import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import {
  AnnotationControls,
  AnnotationControlsProps,
  useSelection,
} from '@spazious/dollhouse-engine';
import { FNode, getNodeFromObject } from '@spazious/dollhouse-fsetup';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { ControlsModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { useAnnotation } from '../../hooks/useAnnotation.js';

export interface AnnotationProps extends Omit<GroupProps, 'position'> {
  selector: string;
  position: Vector2Tuple;
  text?: string;
}

export const Annotation: React.FC<AnnotationProps> = ({
  position,
  selector,
  text,
  ...restProps
}) => {
  const controls = useStore(({ controls }) => controls);
  const { annotations, setStatus } = useAnnotation();
  const { select, selected } = useSelection();
  const { staging, createAction, update, commit } = useStaging();
  const dragging = useRef(false);

  const ref = useRef<Group>(null!);

  const isSelected = useMemo(() => {
    return selected.some((object) => {
      const graphNode = getNodeFromObject(object) as FNode | null;
      return graphNode?.selector === selector;
    });
  }, [selected, selector]);

  const bind = useEventCallback(() => {
    const pointChangeHandler: PickProp<AnnotationControlsProps, 'onPointChange'> = (event) => {
      const { type, last, point } = event;
      if (type === 'update') {
        if (!dragging.current) {
          dragging.current = true;
          staging.prepare();
        }
        update(selector, 'position', [point.x, point.y]);
        if (last) {
          commit(CommanderActionTypesEnum.Move);
          dragging.current = false;
          select([ref?.current]);
        }
      }
    };

    const inputFocusHandler: PickProp<AnnotationControlsProps, 'onInputFocus'> = () => {
      staging.prepare();

      if (annotations.currentSelector === selector) {
        setStatus(annotations.status, '');
      }

      // timeout needed as the SelectionControls resets selection when focus goes out of dollhouse
      // here force selecting annotation after the reset was done by selection controls
      setTimeout(() => {
        select([ref?.current]);
      }, 100);
    };

    const inputChangeHandler: PickProp<AnnotationControlsProps, 'onInputChange'> = (editedText) => {
      staging.add(createAction('update', selector, 'text', editedText));
    };

    const inputBlurHandler: PickProp<AnnotationControlsProps, 'onInputBlur'> = (editedText) => {
      staging.add(createAction('update', selector, 'text', editedText));
      if (editedText.length > 0) {
        commit(CommanderActionTypesEnum.Update);
      } else {
        staging.abort();
      }
    };

    const pointClickHandler: PickProp<AnnotationControlsProps, 'onPointClick'> = () => {
      select([ref.current]);
    };

    return {
      onPointChange: pointChangeHandler,
      onInputFocus: inputFocusHandler,
      onInputChange: inputChangeHandler,
      onInputBlur: inputBlurHandler,
      onPointClick: pointClickHandler,
    };
  });

  return (
    <group {...restProps} ref={ref}>
      <AnnotationControls
        editable={controls === ControlsModeEnum.Zenithal}
        focus={annotations.currentSelector === selector}
        outline={isSelected}
        position={position}
        text={text}
        {...(annotations.drawEnabled ? {} : bind())}
      />
    </group>
  );
};
